import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { Button } from '@/src/renderer/components/ui/Button'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/src/renderer/components/ui/Sheet'
import { useIsActiveTab } from '@/src/renderer/pages/TabStack/ActiveTabContext'
import { trackEvent } from '@/src/renderer/lib/analytics'
import { ANALYTICS_EVENTS } from '@/src/renderer/lib/analytics/events'
import { DEFAULT_STREAK_GRACE_DAYS, type Habit, type HabitFrequency, type LifeSpace } from '@/src/renderer/lib/db/models'
import { useHabits } from '../../hooks/useHabits'
import { useHabitLogs, completedKeySet } from '../../hooks/useHabitLogs'
import { startOfDay } from '../../lib/date'
import { todayProgress } from '../../lib/habitDay'
import { HabitDateStrip } from '../../components/HabitDateStrip'
import { HabitList } from '../../components/HabitList'
import habitsEmptyImage from '../../assets/habits-empty.png'
import './HabitsView.scss'

interface HabitsViewProps {
  lifeSpace: LifeSpace
}

// Minimal create frequencies for this milestone — the full picker (weekday
// chips, day-of-month, interval, time-of-day) lands in the next step.
type CreateKind = 'daily' | 'weekdaysPreset' | 'timesPerWeek' | 'monthly'

function buildFrequency(kind: CreateKind, count: number, day: number): HabitFrequency {
  switch (kind) {
    case 'daily':
      return { kind: 'daily' }
    case 'weekdaysPreset':
      return { kind: 'weekdays', days: [0, 1, 2, 3, 4] }
    case 'timesPerWeek':
      return { kind: 'timesPerWeek', count: Math.max(1, count) }
    case 'monthly':
      return { kind: 'monthly', day: Math.min(31, Math.max(1, day)) }
  }
}

/**
 * The "Gewohnheiten" page for one life space — the day's checklist navigable by
 * date, with streaks, plus a (currently minimal) create sheet.
 */
export const HabitsView: React.FC<HabitsViewProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  const isActive = useIsActiveTab()
  const habits = useHabits(lifeSpace)
  const source = useDataSource()
  const today = useMemo(() => startOfDay(new Date()), [])
  const [selectedDate, setSelectedDate] = useState<Date>(today)

  const habitIds = useMemo(() => (habits ?? []).map(h => h.id), [habits])
  const logsByHabit = useHabitLogs(habitIds)

  // Create sheet state.
  const [createOpen, setCreateOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [freqKind, setFreqKind] = useState<CreateKind>('daily')
  const [count, setCount] = useState(3)
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const createOpenRef = useRef(createOpen)
  createOpenRef.current = createOpen

  const graceDays = DEFAULT_STREAK_GRACE_DAYS

  const completedByHabit = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const h of habits ?? []) map.set(h.id, completedKeySet(logsByHabit?.get(h.id)))
    return map
  }, [habits, logsByHabit])

  const progress = useMemo(
    () => todayProgress(habits ?? [], completedByHabit, today),
    [habits, completedByHabit, today],
  )

  const openCreate = useCallback(() => {
    setTitle('')
    setFreqKind('daily')
    setCount(3)
    setDayOfMonth(1)
    setCreateOpen(true)
  }, [])

  const handleToggle = useCallback(async (habit: Habit, key: string, next: boolean) => {
    try {
      await source.setHabitLog(habit.id, key, next)
      if (next) trackEvent(ANALYTICS_EVENTS.HABIT_COMPLETED, { life_space: lifeSpace })
    } catch (err) {
      console.error('[Habits] toggle failed:', err)
    }
  }, [lifeSpace, source])

  const handleCreateSubmit = useCallback(async () => {
    const trimmed = title.trim()
    if (!trimmed) return
    try {
      await source.createHabit({ title: trimmed, lifeSpace, frequency: buildFrequency(freqKind, count, dayOfMonth) })
      trackEvent(ANALYTICS_EVENTS.HABIT_CREATED, { life_space: lifeSpace, frequency_kind: freqKind })
      setCreateOpen(false)
    } catch (err) {
      console.error('[Habits] create failed:', err)
    }
  }, [title, lifeSpace, freqKind, count, dayOfMonth, source])

  // "A" opens the create sheet — gated on the active tab (TabStack gotcha).
  useEffect(() => {
    if (!isActive) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'a' && e.key !== 'A') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) return
      if (createOpenRef.current) return
      e.preventDefault()
      openCreate()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, openCreate])

  if (habits === undefined) {
    return <div className="habits-view" aria-busy="true" aria-label={t('habits.loading')} />
  }

  const isEmpty = habits.length === 0
  const allDone = progress.total > 0 && progress.done === progress.total

  return (
    <div className="habits-view">
      <div className="habits-view__inner">
        <header className="habits-view__header">
          <div className="habits-view__title-row">
            <h1 className="habits-view__title">{t(`habits.page.${lifeSpace}.title`)}</h1>
            {!isEmpty && (
              <span className={`habits-view__progress ${allDone ? 'habits-view__progress--done' : ''}`}>
                {allDone
                  ? t('habits.progress.allDone')
                  : t('habits.progress.count', { done: progress.done, total: progress.total })}
              </span>
            )}
          </div>
          <p className="habits-view__mantra">{t(`habits.page.${lifeSpace}.description`)}</p>
        </header>

        <HabitDateStrip selectedDate={selectedDate} today={today} onSelect={setSelectedDate} />

        {isEmpty ? (
          <div className="habits-view__empty">
            <img className="habits-view__empty-image" src={habitsEmptyImage} alt="" aria-hidden />
            <Button variant="primary" shortcut={{ kind: 'character', text: 'A' }} onClick={openCreate}>
              {t('habits.cta.empty')}
            </Button>
          </div>
        ) : (
          <>
            <HabitList
              habits={habits}
              logsByHabit={logsByHabit ?? new Map()}
              selectedDate={selectedDate}
              today={today}
              graceDays={graceDays}
              onToggle={handleToggle}
            />
            <div className="habits-view__cta-row">
              <Button variant="secondary" shortcut={{ kind: 'character', text: 'A' }} onClick={openCreate}>
                {t('habits.cta.more')}
              </Button>
            </div>
          </>
        )}
      </div>

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="top" className="habit-sheet">
          <SheetHeader>
            <span className="habit-sheet__sprout" aria-hidden>🌱</span>
            <SheetTitle>{t('habits.sheet.titleCreate')}</SheetTitle>
          </SheetHeader>

          <input
            className="habit-sheet__title-input"
            autoFocus
            value={title}
            placeholder={t('habits.sheet.titlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && title.trim()) handleCreateSubmit()
            }}
          />

          <label className="habit-sheet__field">
            <span className="habit-sheet__field-label">{t('habits.sheet.whenLabel')}</span>
            <select
              className="habit-sheet__select"
              value={freqKind}
              onChange={(e) => setFreqKind(e.target.value as CreateKind)}
            >
              <option value="daily">{t('habits.frequency.daily')}</option>
              <option value="weekdaysPreset">{t('habits.frequency.weekdaysPreset')}</option>
              <option value="timesPerWeek">{t('habits.frequency.timesPerWeek')}</option>
              <option value="monthly">{t('habits.frequency.monthly')}</option>
            </select>
          </label>

          {freqKind === 'timesPerWeek' && (
            <label className="habit-sheet__field">
              <input
                className="habit-sheet__number"
                type="number"
                min={1}
                max={7}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
              />
              <span>{t('habits.frequency.perWeek')}</span>
            </label>
          )}

          {freqKind === 'monthly' && (
            <label className="habit-sheet__field">
              <span>{t('habits.frequency.onDayOfMonth', { day: dayOfMonth })}</span>
              <input
                className="habit-sheet__number"
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
              />
            </label>
          )}

          <SheetFooter>
            <Button variant="tertiary" onClick={() => setCreateOpen(false)}>
              {t('habits.sheet.cancel')}
            </Button>
            <Button variant="primary" disabled={!title.trim()} onClick={handleCreateSubmit}>
              {t('habits.sheet.save')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  )
}
